import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n";
import { LangSwitcher } from "../ui/LangSwitcher";
import { trackSurveyStarted, upsertSurveyAnswer, trackSurveyDropped } from "../lib/analytics";

type Q =
  | { id: string; title: string; subtitle?: string; type: "single"; options: string[] }
  | { id: string; title: string; subtitle?: string; type: "multi"; options: string[] }
  | { id: string; title: string; subtitle?: string; type: "scale"; min: number; max: number; leftLabel: string; rightLabel: string }
  | { id: string; title: string; subtitle?: string; type: "text"; placeholder: string; inputType?: string };

const QUESTIONS: Q[] = [
  { id: "q1", title: "How do you currently choose supplements?", type: "single", options: ["I research myself", "My doctor prescribes them", "I don't take any", "Other"] },
  { id: "q2", title: "Have you had blood work done in the last year?", type: "single", options: ["Yes", "No", "Planning to"] },
  { id: "q3", title: "How interested are you in a personalized plan based on your tests?", subtitle: "1 = not at all, 5 = very interested", type: "scale", min: 1, max: 5, leftLabel: "Not interested", rightLabel: "Very interested" },
  { id: "q4", title: "What matters most to you?", subtitle: "Select all that apply", type: "multi", options: ["Speed", "Personalization", "Price", "Doctor access", "Delivery convenience"] },
  { id: "q5", title: "Do you have any chronic conditions?", type: "single", options: ["None", "1 condition", "2+ conditions", "Prefer not to say"] },
  { id: "q6", title: "Are you currently taking any medication?", type: "single", options: ["No", "Occasionally", "Daily"] },
  { id: "q7", title: "How often do you exercise?", type: "single", options: ["Rarely", "1-2x per week", "3-4x per week", "5+ per week"] },
  { id: "q8", title: "How is your sleep quality?", type: "scale", min: 1, max: 5, leftLabel: "Poor", rightLabel: "Excellent" },
  { id: "q9", title: "What are your primary health goals?", type: "multi", options: ["Energy", "Sleep", "Focus", "Longevity", "Immunity", "Hormonal balance"] },
  { id: "q10", title: "Would you want one place for all your health metrics?", type: "single", options: ["Yes, very much", "Would be nice", "Not needed"] },
  { id: "q11", title: "How much would you pay per month for a personalized AI-driven supplement kit?", type: "single", options: ["Under 30 CHF", "30-60 CHF", "60-100 CHF", "100+ CHF", "Not willing to pay"] },
  { id: "q12", title: "Would you like early access when we launch?", type: "single", options: ["Yes, add me to early access", "Maybe", "No"] },
  { id: "q13", title: "How did you hear about Alvion?", type: "single", options: ["Social media", "Friend", "Search", "Other"] },
  { id: "q14", title: "Email (optional)", subtitle: "For early access and updates", type: "text", placeholder: "you@example.com", inputType: "email" },
];

export function Survey() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);
  const doneRef = useRef(false);
  const stepRef = useRef(0);

  const q = QUESTIONS[step];
  const progress = useMemo(() => ((step + 1) / QUESTIONS.length) * 100, [step]);
  const canNext =
    q && (q.type === "text"
      ? true
      : q.type === "multi"
      ? (answers[q.id]?.length ?? 0) > 0
      : answers[q.id] !== undefined);

  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { doneRef.current = done; }, [done]);

  // track drop-off on unload
  useEffect(() => {
    const onLeave = () => {
      if (startedRef.current && !doneRef.current) trackSurveyDropped(stepRef.current);
    };
    window.addEventListener("pagehide", onLeave);
    return () => { window.removeEventListener("pagehide", onLeave); onLeave(); };
  }, []);

  const persist = (nextAnswers: Record<string, any>, completed: boolean) => {
    const questionId = q!.id;
    upsertSurveyAnswer({
      answers: nextAnswers,
      questionId,
      questionIndex: step,
      answer: nextAnswers[questionId],
      completed,
      droppedAt: completed ? null : step,
      lang,
    });
  };

  const next = () => {
    setDir(1);
    if (!startedRef.current) { trackSurveyStarted(); startedRef.current = true; }
    persist(answers, step === QUESTIONS.length - 1);
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const prev = () => {
    setDir(-1);
    if (step > 0) setStep(step - 1);
  };

  const setAnswer = (v: any) => {
    setAnswers((a) => {
      const nxt = { ...a, [q!.id]: v };
      if (!startedRef.current) { trackSurveyStarted(); startedRef.current = true; }
      return nxt;
    });
  };
  const toggleMulti = (opt: string) => {
    const cur: string[] = answers[q!.id] ?? [];
    setAnswer(cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  return (
    <div className="min-h-screen bg-[var(--color-cream)] flex flex-col">
      <header className="px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-[var(--color-forest-deep)]" />
          <span className="font-logo text-sm text-[var(--color-forest-deep)]">ALVION</span>
        </Link>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          <button onClick={() => nav("/")} className="text-sm text-[var(--color-muted)] hover:text-[var(--color-ink)]">
            {t("nav.close")}
          </button>
        </div>
      </header>

      {!done && (
        <div className="px-8">
          <div className="max-w-2xl mx-auto h-[3px] bg-[var(--color-sage)]/25 rounded-full overflow-hidden">
            <motion.div
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-full bg-[var(--color-moss)]"
            />
          </div>
          <div className="max-w-2xl mx-auto mt-2 flex justify-between text-xs text-[var(--color-muted)]">
            <span>{t("survey.question")} {step + 1} {t("survey.of")} {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={dir}>
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <h1 className="font-serif text-5xl text-[var(--color-ink)]">{t("survey.thanks.title")}</h1>
                <p className="mt-4 text-[var(--color-muted)]">{t("survey.thanks.sub")}</p>
                <Link to="/" className="pill-btn mt-10 inline-flex">{t("survey.home")}</Link>
              </motion.div>
            ) : (
              <motion.div
                key={q.id}
                custom={dir}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -dir * 60 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              >
                <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[var(--color-ink)]">{q.title}</h2>
                {q.subtitle && <p className="mt-2 text-sm text-[var(--color-muted)]">{q.subtitle}</p>}

                <div className="mt-10 space-y-3">
                  {q.type === "single" &&
                    q.options.map((opt) => {
                      const active = answers[q.id] === opt;
                      return (
                        <button
                          key={opt}
                          onClick={() => setAnswer(opt)}
                          className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                            active
                              ? "border-[var(--color-moss)] bg-[var(--color-sage-light)]/50"
                              : "border-[var(--color-ink)]/15 bg-white/40 hover:bg-white/80"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}

                  {q.type === "multi" &&
                    q.options.map((opt) => {
                      const active = (answers[q.id] ?? []).includes(opt);
                      return (
                        <button
                          key={opt}
                          onClick={() => toggleMulti(opt)}
                          className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between ${
                            active
                              ? "border-[var(--color-moss)] bg-[var(--color-sage-light)]/50"
                              : "border-[var(--color-ink)]/15 bg-white/40 hover:bg-white/80"
                          }`}
                        >
                          <span>{opt}</span>
                          <span className={`w-5 h-5 rounded border ${active ? "bg-[var(--color-moss)] border-[var(--color-moss)]" : "border-[var(--color-ink)]/30"}`} />
                        </button>
                      );
                    })}

                  {q.type === "scale" && (
                    <div className="flex gap-3 justify-between items-center pt-4">
                      {Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i).map((n) => {
                        const active = answers[q.id] === n;
                        return (
                          <button
                            key={n}
                            onClick={() => setAnswer(n)}
                            className={`flex-1 aspect-square rounded-full border font-serif text-2xl transition-all ${
                              active
                                ? "bg-[var(--color-moss)] border-[var(--color-moss)] text-white scale-110"
                                : "border-[var(--color-ink)]/20 text-[var(--color-ink)] hover:bg-white/60"
                            }`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {q.type === "scale" && (
                    <div className="flex justify-between text-xs text-[var(--color-muted)] mt-3">
                      <span>{q.leftLabel}</span>
                      <span>{q.rightLabel}</span>
                    </div>
                  )}

                  {q.type === "text" && (
                    <input
                      type={q.inputType ?? "text"}
                      placeholder={q.placeholder}
                      value={answers[q.id] ?? ""}
                      onChange={(e) => setAnswer(e.target.value)}
                      className="w-full px-5 py-4 rounded-xl border border-[var(--color-ink)]/15 bg-white/60 focus:outline-none focus:border-[var(--color-moss)]"
                    />
                  )}
                </div>

                <div className="mt-10 flex items-center justify-between">
                  <button
                    onClick={prev}
                    disabled={step === 0}
                    className="text-[var(--color-muted)] disabled:opacity-30 text-sm"
                  >
                    ← {t("survey.back")}
                  </button>
                  <button
                    onClick={next}
                    disabled={!canNext}
                    className="pill-btn disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {step === QUESTIONS.length - 1 ? t("survey.submit") : t("survey.next")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
